/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import {
  Controller, FieldErrors, FieldValues, SubmitHandler, UseFormRegister,
} from 'react-hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  AlertTitle,
  TextField,
  FormControl,
  Box,
  Button,
  Stack,
  FormHelperText,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CountrySetting, Destination, Partner, TripRequest,
} from '@marageti/z4-sdk/lib/travel';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import {
  MultiSelectorAutocomplete,
  useForm,
  getFieldProps,
  getFormProps,
  getSubmitButtonProps,
  ChipSelectList,
  ChipOption,
} from '@marageti/z4-lib';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addYears, addDays } from 'date-fns';
import * as z from 'zod';
import CreateTravelerModal from './create-traveler-modal';
import TravelerLookupInput from '../traveler-lookup-input';
import PartnerSelector from '../partner-lookup-input';
import CurrencyInput from '../currency-input';

const AdultAgeGroupsChips: ChipOption[] = [
  { key: '18-30', label: '18-30', value: '18-30' },
  { key: '31-50', label: '31-50', value: '31-50' },
  { key: '51-64', label: '51-64', value: '51-64' },
  { key: '65+', label: '65+', value: '65+' },
];

const ChildAgeGroupsChips: ChipOption[] = [
  { key: '0-2', label: '0-2', value: '0-2' },
  { key: '3-7', label: '3-7', value: '3-7' },
  { key: '8-12', label: '8-12', value: '8-12' },
  { key: '13-17', label: '13-17', value: '13-17' },
];

const DestinationsSchema = z.object({
  countries: z.array(z.string()),
  createdAt: z.string(),
  id: z.string(),
  name: z.string(),
  served: z.boolean(),
  updateId: z.string(),
  updatedAt: z.string(),
  minNights: z.number().optional(),
});

const TripCreateSchema = z.object({
  traveler: z.object({
    id: z.string(),
    fullName: z.string(),
    email: z.string(),
    referringPartnerId: z.string().optional(),
  }).passthrough().required(),
  destinations: z.array(DestinationsSchema).min(1, 'Select at least one destination'),
  departureDate: z.date().min(new Date(), 'Departure Date should be a future date'),
  totalNights: z.number({ invalid_type_error: 'Enter a number of nights' }).min(5, 'total number of nights must be at least 5'),
  totalBudget: z.number({ invalid_type_error: 'Enter a total budget' }).min(1, 'Enter a total budget'),
  numberOfAdults: z.number({ invalid_type_error: 'Enter the number of adults' }).min(1, 'Enter the number of adults'),
  numberOfChildren: z.number({ invalid_type_error: 'Enter the number of children' }),
  adultAgeGroups: z.array(z.string()).optional(),
  childAgeGroups: z.array(z.string()).optional(),
  tripDescription: z.string().min(1, { message: 'Enter a description' }),
  referringPartner: z.object({
    id: z.string(),
    name: z.string(),
  }).optional(),
  sendEmail: z.boolean().optional(),
}).refine(
  (data) => {
    let minNightCount = 5;
    data.destinations.forEach((val) => {
      const minNights = val?.minNights || 5;
      if (minNights > minNightCount) minNightCount = minNights;
    });
    return data.totalNights >= minNightCount;
  },
  { message: 'Total nights too low for selected destination', path: ['totalNights'] },
);

type TripRequestFormProps = {
  destinationsList: Destination[];
  onCancel: () => void;
  onSubmit: SubmitHandler<FieldValues>;
  submissionError?: ApiError;
  defaultValues?: TripRequest | { [k: string]: any };
  validateOnMount?: boolean;
  handleCreateTraveler?: (data: any, callback?: (v: any) => void) => void;
  travelerCreateError?: ApiError;
  showSendEmail?: boolean;
  defaultCountrySettings?: CountrySetting;
  editMode?: boolean;
};

const minDate = addDays(new Date(), 1);

const DescriptionField = ({ register, errors }: { register: UseFormRegister<FieldValues>, errors: FieldErrors<FieldValues> }) => (
  <TextField
    label="Description"
    multiline
    minRows={4}
    maxRows={20}
    {...register('tripDescription')}
    {...getFieldProps('tripDescription', errors)}
  />
);

const TripRequestForm = ({
  // required for edit and create
  destinationsList,
  onCancel,
  onSubmit,
  submissionError = undefined,
  validateOnMount = false,
  defaultValues = {},
  // create specific
  handleCreateTraveler = undefined,
  travelerCreateError = undefined,
  showSendEmail = false,
  defaultCountrySettings = undefined,
  editMode = false,
}: TripRequestFormProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isValid },
    register,
    trigger,
    watch,
    setValue,
  } = useForm({
    defaultValues,
    resolver: zodResolver(TripCreateSchema),
  });
  const maxDate = addYears(new Date(), 10);
  useEffect(() => {
    if (validateOnMount) {
      trigger();
    }
  }, [validateOnMount]);

  const [travelerModalOpen, setTravelerModalOpen] = useState(false);

  const watchBudget = watch('totalBudget');
  const watchTotalNights = watch('totalNights');
  const watchNumberOfAdults = watch('numberOfAdults');
  const watchNumberOfChildren = watch('numberOfChildren');
  const watchTraveler = watch('traveler');

  const disablePRL = !!watchTraveler?.referringPartnerId;

  // Takes total budget, total nights, number of adults and children and returns a string
  // that describes the budget per person and budget per person per day
  const getBudgetText = () => {
    if (errors.totalBudget) {
      return errors.totalBudget.message as string | undefined;
    }
    if (!watchBudget) return '';
    if (!watchTotalNights) return '';
    if (!watchNumberOfAdults) return '';
    const numberOfTravelers = watchNumberOfAdults + (watchNumberOfChildren || 0);
    return `${(watchBudget / numberOfTravelers).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })} per person - ${(watchBudget / (numberOfTravelers * watchTotalNights)).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })} PPPD`;
  };

  const helperText = watchTraveler?.id
    ? ''
    // eslint-disable-next-line react/jsx-one-expression-per-line
    : <Typography variant="caption">New Traveler? <span onClick={() => setTravelerModalOpen(true)} style={{ textDecoration: 'underline', cursor: 'pointer' }}>Create Account</span> </Typography>;

  return (
    <>
      {!editMode && (
      <Box style={{ marginBottom: '-1rem' }}>
        <TravelerLookupInput
          readOnly={!handleCreateTraveler}
          key={watchTraveler?.id}
          value={watchTraveler}
          onChange={(_: any, value: Partner) => {
            setValue('traveler', value, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
          inputProps={{
            label: 'Traveler',
            helperText,
          }}
        />
      </Box>
      )}
      <Box
        component="form"
        name="trip-request-form"
        {...getFormProps(handleSubmit(onSubmit))}
        sx={{
          flex: 1,
          width: '100%',
        }}
      >
        <Stack
          spacing={2}
          sx={editMode ? {
            flex: 1,
            maxHeight: '60vh',
            overflow: 'auto',
            pt: 1,
            pr: 3,
            mx: 3,
            mr: 0,
            mb: 0,
          } : {}}
        >
          <Stack spacing={1}>
            {editMode && <DescriptionField register={register} errors={errors} />}
            <Controller
              name="destinations"
              control={control}
              render={({ field }) => (
                <MultiSelectorAutocomplete
                  {...field}
                  label="Destinations"
                  optionEntities={destinationsList || []}
                  valueFieldName="id"
                  labelFieldName="name"
                  onChange={(_: any, data: any[]) => {
                    field.onChange(data);
                    const currentNights = watch('totalNights') || 0;
                    const maxMinNights = Math.max(...data.map((dest) => dest?.minNights), defaultCountrySettings?.minNights ?? 0);

                    if (currentNights < maxMinNights) {
                      setValue('totalNights', maxMinNights, { shouldValidate: true });
                    }
                  }}
                  {...getFieldProps('destinations', errors)}
                />
              )}
            />
          </Stack>
          <Stack spacing={1}>
            <Stack direction="row" spacing={2}>
              <Controller
                name="departureDate"
                control={control}
                render={({
                  field: {
                    onChange, onBlur, value, ref, name,
                  },
                }) => (
                  <DatePicker
                    disablePast
                    label="Departure (MM/DD/YYYY)"
                    onChange={onChange}
                    value={value}
                    minDate={minDate}
                    maxDate={maxDate}
                    slotProps={{
                      textField: { name, onBlur, error: !!errors.departureDate },
                    }}
                    inputRef={ref}
                    sx={{ flex: 1, minWidth: '125px' }}
                  />
                )}
              />
              <TextField
                type="number"
                label="Total Nights"
                sx={{ flex: 1, flexShrink: 1 }}
                inputProps={{ min: 5 }}
                {...register('totalNights', { valueAsNumber: true })}
                {...getFieldProps('totalNights', errors)}
              />
            </Stack>
            {errors.departureDate && <FormHelperText error>{errors.departureDate.message as string}</FormHelperText>}
            <Box>
              <Controller
                control={control}
                name="totalBudget"
                render={({ field }) => (
                  <CurrencyInput
                    label="Total Budget"
                    fullWidth
                    required
                    {...field}
                    {...getFieldProps('totalBudget', errors)}
                    defaultValue={defaultValues?.totalBudget}
                    numericFormatProps={{
                      allowNegative: false,
                      currencyCode: 'USD',
                    }}
                    helperText={getBudgetText()}
                  />
                )}
              />
            </Box>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              type="number"
              label="Adults"
              sx={{ flex: 1 }}
              inputProps={{ min: 0 }}
              {...register('numberOfAdults', { valueAsNumber: true })}
              {...getFieldProps('numberOfAdults', errors)}
            />
            <TextField
              type="number"
              label="Children"
              sx={{ flex: 1 }}
              inputProps={{ min: 0 }}
              {...register('numberOfChildren', { valueAsNumber: true })}
              {...getFieldProps('numberOfChildren', errors)}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <FormControl>
                <Controller
                  name="adultAgeGroups"
                  control={control}
                  render={({ field }) => (
                    <ChipSelectList
                      chipOptions={AdultAgeGroupsChips}
                      chipSize="large"
                      label="Adult Ages"
                      onChange={(data: string[]) => {
                        field.onChange(data);
                        trigger('adultAgeGroups');
                      }}
                      selectedChips={field.value || []}
                    />
                  )}
                />
                {errors.adultAgeGroups && <FormHelperText error>{errors.adultAgeGroups.message as string}</FormHelperText>}
              </FormControl>
            </Box>
            {watch('numberOfChildren') > 0 && (
              <Box sx={{ flex: 1 }}>
                <FormControl>
                  <Controller
                    name="childAgeGroups"
                    control={control}
                    render={({ field }) => (
                      <ChipSelectList
                        chipOptions={ChildAgeGroupsChips}
                        chipSize="large"
                        label="Children Ages"
                        onChange={(data: string[]) => {
                          field.onChange(data);
                          trigger('childAgeGroups');
                        }}
                        selectedChips={field.value || []}
                      />
                    )}
                  />
                  {errors.childAgeGroups && <FormHelperText error>{errors.childAgeGroups.message as string}</FormHelperText>}
                </FormControl>
              </Box>
            )}
          </Stack>
          {!editMode && <DescriptionField register={register} errors={errors} />}
          <PartnerSelector
            value={watch('referringPartner')}
            onChange={(_: any, value: Partner) => {
              setValue('referringPartner', value, {
                shouldValidate: true,
              });
            }}
            inputProps={{
              label: 'PRL - One Time',
              placeholder: 'Search for a partner',
              disabled: disablePRL,
              sx: { mt: 1.5, mb: editMode ? 3.5 : 0 },
            }}
          />
          {showSendEmail && (
            <Controller
              name="sendEmail"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Box
                  sx={{
                    width: '100%',
                    backgroundColor: '#F0F4FF',
                    borderRadius: 1,
                    padding: 1,
                  }}
                >
                  <FormControlLabel
                    style={{ width: '100%', justifyContent: 'space-between', margin: '0 0 0 0.5rem' }}
                    value="start"
                    componentsProps={{ typography: { variant: 'body2', style: { color: '#0D1718', fontSize: '1rem' } } }}
                    control={<Switch color="primary" checked={value} onChange={onChange} />}
                    label="Send Trip Request Confirmation email?"
                    labelPlacement="start"
                  />
                </Box>
              )}
            />
          )}
          {submissionError && (
            <Alert severity="error">
              <AlertTitle>There was an error on our end.</AlertTitle>
              {`Code: ${submissionError.code}: ${submissionError.message}`}
              <br />
              {submissionError.id && `ID: ${submissionError.id}`}
            </Alert>
          )}
        </Stack>
        <Stack
          direction="row"
          spacing={2}
          onMouseEnter={() => trigger()}
          sx={editMode ? (theme) => ({
            borderTop: `1px solid ${theme.palette.divider}`, py: 2.5, px: 3,
          }) : { mt: '24px' }}
        >
          <LoadingButton
            variant="contained"
            type="submit"
            {...getSubmitButtonProps(isSubmitting, isValid)}
          >
            {editMode ? 'Save' : 'Submit'}
          </LoadingButton>
          <Button variant="outlined" onClick={onCancel}>Cancel</Button>
        </Stack>
      </Box>
      {handleCreateTraveler && (
        <CreateTravelerModal
          handleSubmit={
            (data) => handleCreateTraveler(data, (v) => {
              setTravelerModalOpen(false);
              setValue('traveler', v, { shouldValidate: true, shouldDirty: true });
            })
          }
          open={travelerModalOpen}
          onCancel={() => setTravelerModalOpen(false)}
          submissionError={travelerCreateError}
        />
      )}
    </>
  );
};

export default TripRequestForm;
